import { AppInsights } from 'applicationinsights-js';
import { isEqual, truncate } from 'lodash';
import { ClientConfiguration } from 'src/app/app.config';
import { environment } from 'src/environments/environment';
import { uuid } from './uuid';

const appInsightsMaxPropLength = 8192;

export function createSafeProp<T extends {}>(value: string | T) {
  const t = (val: string) =>
    truncate(val, { omission: '... [truncated]', length: appInsightsMaxPropLength });
  if (typeof value === 'string') {
    return t(value);
  }
  try {
    if (isEqual(value, {})) {
      return null;
    }
    const json = JSON.stringify(value);
    return t(json);
  } catch (err) {
    return `Error serializing/truncating json property: ${err.message}`;
  }
}

export function createSafeProps<T extends {}>(val: T): { [name: string]: string } {
  const result: { [name: string]: string } = {};
  Object.entries(val).forEach(([key, value]) => {
    const propVal = createSafeProp(value as string);
    if (propVal != null) {
      result[String(key)] = propVal;
    }
  });
  return result;
}

export function patchEnvelopeProps<T extends {}>(
  env: Microsoft.ApplicationInsights.IEnvelope,
  props: T,
) {
  if (env.data.baseData != null) {
    const oldProps = env.data.baseData.properties;
    const newProps = createSafeProps(props);
    env.data.baseData.properties =
      oldProps != null
        ? {
            ...oldProps,
            ...newProps,
          }
        : newProps;
    return true;
  }
  return false;
}

const tabId = uuid();
export interface ApplicationInsightsConfiguration {
  enabled: boolean;
  instrumentationKey: string | null;
}

export function bootstrapApplicationInsights({
  applicationInsights: { enabled, instrumentationKey },
  tcs: { graphQLUri },
}: ClientConfiguration) {
  if (enabled) {
    if (instrumentationKey == null) {
      // tslint:disable-next-line: no-console
      console.warn('Application Insights is enabled but instrumentation key was not provided.');
    } else if (AppInsights.downloadAndSetup == null) {
      // tslint:disable-next-line: no-console
      console.warn('Application insights was enabled but SDK was not loaded properly.');
    } else {
      const config: Microsoft.ApplicationInsights.IConfig = {
        instrumentationKey,
        // We set correlation headers manually - App Insights assumes non-spa environment
        disableCorrelationHeaders: true,
        disableExceptionTracking: true,
        autoTrackPageVisitTime: false,
        enableDebug: !environment.production,
        maxAjaxCallsPerView: -1,
        // disabling cookie use locally to avoid this exception: https://github.com/Microsoft/ApplicationInsights-JS/issues/482
        isCookieUseDisabled: !environment.production,
        verboseLogging: !environment.production,
      };
      AppInsights.downloadAndSetup(config);
      AppInsights.queue.push(() => {
        // Add tab ID
        AppInsights.context.addTelemetryInitializer((env) => {
          if (env?.data?.baseData?.properties != null) {
            env.data.baseData.properties.tabId = tabId;
          }
        });

        // Don't track network exceptions
        AppInsights.context.addTelemetryInitializer((env) => {
          if (env.data.baseType === 'ExceptionData') {
            const isNetworkException =
              env.data.baseData.exceptions.find(
                (e: { typeName?: string; message?: string }) =>
                  e.typeName === 'Error' &&
                  e.message != null &&
                  e.message.includes('Network error'),
              ) != null;
            if (isNetworkException) {
              /*
              We can't add very much helpful information to network exceptions, so we don't send
              exception telemetry to application insights to reduce noise. Failed network requests
              are observed in application insights via dependency tracking, where we have the ability
              to add more helpful information about the request and errors.
               */
              return false;
            }
          }
        });

        // Don't automatically track GraphQL dependencies (we do that manually)
        AppInsights.context.addTelemetryInitializer((env) => {
          if (env.data?.baseType === 'RemoteDependencyData' && env.data?.baseData?.target != null) {
            const target = env.data.baseData.target;
            const isGraphQLRequest = graphQLUri.includes(target);
            const properties: { [name: string]: string | undefined } | undefined =
              env.data.baseData.properties;
            if (isGraphQLRequest) {
              const trackGraphQLRequest = properties != null && properties.__trackMe === 'true';
              if (!trackGraphQLRequest) {
                // We ignore the default dependency tracking, instead only honoring calls to
                // track dependencies that we've explicitly tracked via
                // app-insights-dependeny-tracking.apollo-link.ts
                return false;
              }

              delete env.data.baseData.properties.__trackMe;
              env.data.baseData.type = 'graphql';
              let operationNameSet = false;
              // tslint:disable-next-line: no-non-null-assertion
              const { gqlOperationType, gqlOperationName, variables } = properties!;
              env.data.baseData.data = variables;
              if (gqlOperationType != null && gqlOperationName != null) {
                const operationName = gqlOperationType + ' ' + gqlOperationName;
                env.tags['ai.operation.name'] = operationName;
                env.data.baseData.name = operationName;
                operationNameSet = true;
              }
              const operationId = env.data.baseData.id;
              env.tags['ai.operation.id'] = operationId;
              if (!operationNameSet) {
                let operationName: string | undefined = env.data.baseData.data;
                if (typeof operationId === 'string') {
                  operationName = operationId.split('-')[0];
                }
                env.tags['ai.operation.name'] = operationName;
                env.data.baseData.name = operationName;
              }
            } else {
              let operationName = 'website-unknown';
              if (target.includes('pndsn.com')) {
                env.data.baseData.type = 'PubNub';
                // pubnub
                if (target.includes('heartbeat')) {
                  operationName = 'pubnub-heartbeat';
                } else {
                  operationName = 'pubnub';
                }
                const resultCode = Number(env.data.baseData.resultCode);
                if (resultCode === 0) {
                  // Don't log PubNub dependencies with a result code of 0.
                  // PubNub cancels requests as part of the unsubscribe process, resulting in 0 resultCode
                  // Logging these events as failed depenencies just adds noise to the logs
                  return false;
                }
              }

              const opId = operationName + '-' + uuid();
              env.tags['ai.operation.id'] = opId;
              env.tags['ai.operation.name'] = operationName;
            }
          }
        });

        AppInsights.context.addTelemetryInitializer((env) => {
          if (
            env.data.baseType === 'ExceptionData' &&
            env.data.baseData?.exceptions[0]?.message.includes('command failed:')
          ) {
            // This will be tracked via dependencies. Suppressing exceptions to reduce noise.
            return false;
          }
        });

        // Set cloud_roleName
        AppInsights.context.addTelemetryInitializer((env) => {
          env.tags['ai.device.roleName'] = 'website';
        });

        // Always include current href
        AppInsights.context.addTelemetryInitializer((env) => {
          patchEnvelopeProps(env, { href: window.location.href });
        });

        AppInsights.context.addTelemetryInitializer((env) => {
          const operationId = env.data.baseData.operationId;
          if (operationId != null) {
            env.tags['ai.operation.id'] = operationId;
          }
        });

        AppInsights.context.addTelemetryInitializer((env) => {
          const operationId: string | null = env.data.baseData.properties.operationId;
          if (operationId != null) {
            delete env.data.baseData.properties.operationId;
            env.tags['ai.operation.id'] = operationId;
          }
        });
      });
    }
  }
}
